/* eslint-disable max-lines */
import { Promotion } from 'types/utils';
import { GTM_CHECK_ATTEMPT_COUNT, PROD_HOST_NAME, STORAGE_KEY } from './analytics-utils-constants';
import {
  AddToCartProduct,
  EventQueue,
  ImpressionProduct,
  ProductEventParams,
} from './analytics-utils-types';

export enum ClickButtonElementName {
  SwitchForward = 'switch-forward',
  SwitchBackward = 'switch-backward',
}

/**
 * Send events to analytics
 * @class
 * @public
 */

class AnalyticsUtils {
  hasFlagLogAnalytics: string | null = null;

  isGtmCheckLaunched = false;

  isGtmLoaded = false;

  eventQueue: EventQueue[] = [];

  constructor() {
    if (!__IS_SERVER__) {
      this.gtmCheck();
      this.hasFlagLogAnalytics = localStorage.getItem(STORAGE_KEY);
      if (!this.hasFlagLogAnalytics) {
        localStorage.setItem(STORAGE_KEY, '');
      }
    }
  }

  /**
   * Start check if gtmDom loaded
   */
  gtmCheck() {
    this.isGtmCheckLaunched = true;

    if (window.location.hostname !== PROD_HOST_NAME) {
      this.isGtmLoaded = true;
      return;
    }

    let attempt = 0;
    const interval = setInterval(() => {
      const gtmDom = window.google_tag_manager?.dataLayer?.gtmDom;
      if (gtmDom) {
        clearInterval(interval);
        this.isGtmLoaded = true;
        if (this.eventQueue.length) {
          this.eventQueue.forEach(({ event, params }) => {
            this.dispatchEvent(event, params);
          });
        }

        return;
      }

      if (attempt > GTM_CHECK_ATTEMPT_COUNT) {
        clearInterval(interval);
        this.isGtmLoaded = false;
        return;
      }

      attempt += 1;
    }, 1000);
  }

  /**
   * Send event with params
   * @param {string} event name of event
   * @param {*} params any data
   * @public
   */
  public sendEvent(event: string, params: unknown) {
    if (!this.isGtmLoaded && !this.isGtmCheckLaunched) {
      this.gtmCheck();
    }

    if (!this.isGtmLoaded && this.isGtmCheckLaunched) {
      this.eventQueue.push({ event, params });
      return;
    }

    this.dispatchEvent(event, params);
  }

  /**
   * Dispatch event with params
   * @param {string} event name of event
   * @param {*} params any data
   * @public
   */
  private dispatchEvent(event: string, params: unknown) {
    if (!!this.hasFlagLogAnalytics) {
      // eslint-disable-next-line no-console
      console.log(event, params);
    }

    document.dispatchEvent(new CustomEvent(event, { detail: params }));
  }

  /**
   * Отправка события вида "GA:clickButton"
   * @param {string} elementBlock - Блок, в котором расположен элемент
   * @param {string} elementText  - Текст кнопки
   * @param {string} elementName  - Название блока
   * @param {string} [elementVariant] - Название товара
   */
  public clickButtonEvent = (elementBlock: string, elementText: string, elementName: string, elementVariant?: string) =>
    this.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock,
      elementName,
      elementText,
      ...(elementVariant ? { elementVariant } : {}),
    });

    /**
   * Отправка события вида "GA:clickButton" при клике по ссылке
   * @param {string} elementBlock     - Блок, в котором расположен элемент
   * @param {string} elementName      - Текст кнопки
   * @param {string} elementText      - Название блока
   * @param {string} [elementLink]    - Ссылка перехода
   */
  public linkClickButtonEvent = (elementBlock: string, elementName: string, elementText: string, elementLink?: string) =>
    this.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock,
      elementName,
      elementText,
      ...(elementLink ? { elementLink } : {}),
    });

  /**
   * Отправка события вида "GA:clickButton" для комплектов
   * @param {string} elementBlock - Блок, в котором расположен элемент
   * @param {string} elementName  - Название блока
   * @param {string} elementText  - Текст кнопки
   * @param {string} elementPosition  - Порядковый номер комплекта
   * @param {string} komplektyId  - Идентификатор комплекта
   */
  public clickButtonCollectionEvent = (
    elementBlock: string,
    elementName: string,
    elementText: string,
    elementPosition: string,
    komplektyId: string
  ) =>
    this.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock,
      elementName,
      elementText,
      elementPosition,
      komplektyId,
    });

  /**
   * Отправка события вида "GA:promoClick"
   * @param {string} elementBlock  - Блок, в котором расположен элемент
   * @param {Promotion} promotion - Объект с данными
   */
  public promoClickEvent = (elementBlock: string, promotion: Promotion) =>
    this.sendEvent('GA:promoClick', {
      elementType: 'banner',
      elementBlock,
      promotions: [promotion],
    });

  /**
   * Отправка события вида "GA:addToCart"
   * @param {string} elementBlock      - Блок, в котором расположен элемент
   * @param {string} elementText       - Название блока
   * @param {AddToCartProduct} product - Продукт, добавленный в корзину
   */
  public addToCartEvent = (elementBlock: string, elementText: string, product: AddToCartProduct) =>
    this.sendEvent('GA:addToCart', {
      elementBlock,
      elementText,
      elementType: 'button',
      ...product,
    });

  /**
   * Отправка события вида "GA:impressions"
   * @param {string} elementBlock       - Блок, в котором расположен элемент
   * @param {string} elementText        - Название блока
   * @param {ImpressionProduct} product - Продукт, попавший во viewport
   */
  public impressionsEvent = (elementBlock: string, elementText: string, product: ImpressionProduct) =>
    this.sendEvent('GA:impressions', {
      elementType: 'page',
      elementBlock,
      elementText,
      ...product,
    });

  /**
   * Отправка события вида "GA:show"
   * @param {string} elementBlock       - Блок, в котором расположен элемент
   * @param {string} elementName        - Название элемента
   * @param {string} elementText        - Название блока
   */
  public showEvent = (elementBlock: string, elementName: string, elementText: string) =>
    this.sendEvent('GA:show', {
      elementType: 'block',
      elementBlock,
      elementName,
      elementText,
    });

  /**
   * Отправка события вида "GA:clickProduct"
   * @param {ProductEventParams} params параметры события
   */
  public clickProductEvent = (params: ProductEventParams) => this.sendEvent('GA:clickProduct', params);
}

export const analyticsUtils = new AnalyticsUtils();
